import Lottie from 'lottie-react';
import React, { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { BodyText, FlexContainer } from '@placer-ui/components';
import {
    useResolverJobResultsQuery,
    useResolverJobStatusQuery,
} from 'features/offline-reports/support-requests/components/support-requests-modals/queries/resolver-bridge-queries';
import { useSendSupportSplunkEvent } from 'features/offline-reports/support-requests/hooks/use-send-support-splunk-event';
import {
    type PropertyFormValues,
    RequestPropertyFormProps,
} from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/types';
import { useOpenSupportRequest } from 'features/offline-reports/support-requests/components/support-requests-modals/support-modal-manager';
import { useCreateResolverJobMutationStatus } from 'features/offline-reports/support-requests/components/support-requests-modals/queries/hooks/use-create-resolver-job-mutation-status';

import animationData from 'components/assets/lottie/animated-loading-data.json';
import styles from './coverage-check.module.scss';

const messages = [
    'Preparing your data for our coverage check.',
    'Matching the data you uploaded',
    'Matching complete! Loading your data...',
];

export const SupportRequestsCoverageCheckModal = ({
    onNextHandler,
}: Partial<RequestPropertyFormProps>) => {
    const formController = useFormContext<PropertyFormValues>();
    const [currentTextIndex, setCurrentTextIndex] = useState(0);
    const isNavigate = useRef(false);
    const sendSplunkEvent = useSendSupportSplunkEvent();
    const { openSupportRequest } = useOpenSupportRequest();
    const startTimeRef = useRef(Date.now());

    const inputTypeField = formController.watch('inputType');

    const { isSuccess: isCreateResolverJobSuccess, isError: isCreateResolverJobError } =
        useCreateResolverJobMutationStatus();
    const { data: resolverJobStatus, isError: isResolverJobStatusError } =
        useResolverJobStatusQuery();
    const { isSuccess: isResolverResultsReady, isError: isResolverJobResultsError } =
        useResolverJobResultsQuery();

    useEffect(() => {
        if (isCreateResolverJobSuccess) {
            setCurrentTextIndex(1);
        }
        if (resolverJobStatus?.status === 'Ready') {
            setCurrentTextIndex(2);
        }
        if (isResolverResultsReady) {
            const load_time = Date.now() - startTimeRef.current;

            sendSplunkEvent({
                action: 'run-resolver',
                value: inputTypeField,
                load_time,
            });

            if (!isNavigate.current) {
                isNavigate.current = true;
                onNextHandler?.();
            }
        }
        if (isResolverJobStatusError || isCreateResolverJobError || isResolverJobResultsError) {
            openSupportRequest({ flowType: 'resolverProcessFailed', retryType: 'property' });
        }
    }, [
        inputTypeField,
        isCreateResolverJobError,
        isCreateResolverJobSuccess,
        isResolverJobResultsError,
        isResolverJobStatusError,
        isResolverResultsReady,
        onNextHandler,
        openSupportRequest,
        resolverJobStatus?.status,
        sendSplunkEvent,
    ]);

    return (
        <FlexContainer
            gap="12px"
            flexDirection="column"
            alignItems="center"
            flexGrow={1}
            justifyContent="center"
        >
            <div className={styles.loaderWrapper}>
                <Lottie animationData={animationData} data-testid="coverage-check-loader" />
            </div>
            <BodyText variant="semi-bold">{messages[currentTextIndex]}</BodyText>
        </FlexContainer>
    );
};
